<template>
  <div class="home mt-5">
    <b-container class="list-clients-wrapper">

      <!-- Header -->
      <b-row class="mb-3 align-items-center">
        <b-col>
          <h5 class="mb-0"><i class="fa fa-cogs mr-2"></i>Configurador de GenIAs</h5>
          <small class="text-muted">Configure nome, instrucoes, modelo e parametros de cada GenIA</small>
        </b-col>
        <b-col cols="auto" v-if="!editingAgent && !creatingAgent">
          <b-button variant="success" size="sm" @click="startCreate">
            <i class="fa fa-plus mr-1"></i> Nova GenIA
          </b-button>
        </b-col>
      </b-row>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2 text-muted">Carregando agentes...</p>
      </div>

      <!-- Agent List (cards) -->
      <b-row v-else-if="!editingAgent && !creatingAgent">
        <b-col md="4" v-for="agent in agents" :key="agent.id" class="mb-3">
          <b-card class="shadow-sm agent-card h-100" :class="{ 'agent-inactive': !agent.active }" @click="openEditor(agent.id)">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <h6 class="mb-0">{{ agent.label }}</h6>
              <div class="d-flex align-items-center">
                <b-badge v-if="!agent.active" variant="danger" pill class="mr-1"
                  v-b-tooltip.hover title="Esta GenIA esta desativada e nao aparece no chat.">
                  Desativada
                </b-badge>
                <b-badge v-if="agent.isDynamic" variant="primary" pill
                  v-b-tooltip.hover title="Esta GenIA foi criada pelo admin.">
                  Criada
                </b-badge>
                <b-badge v-else-if="agent.hasOverride" variant="warning" pill
                  v-b-tooltip.hover title="Esta GenIA possui configuracoes alteradas pelo admin.">
                  Customizada
                </b-badge>
                <b-badge v-else variant="light" pill
                  v-b-tooltip.hover title="Esta GenIA usa a configuracao original do sistema, sem personalizacoes.">
                  Config padrao
                </b-badge>
                <b-form-checkbox switch size="sm" class="ml-2 mb-0"
                  :checked="agent.active" @change="toggleActive(agent, $event)"
                  @click.native.stop v-b-tooltip.hover :title="agent.active ? 'Desativar' : 'Ativar'" />
              </div>
            </div>
            <p class="text-muted small mb-2">{{ agent.description }}</p>
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <b-badge variant="info" pill>{{ agent.model }}</b-badge>
                <b-badge v-if="agent.knowledgeSource === 'legacy'" variant="secondary" pill class="ml-1"
                  v-b-tooltip.hover title="Possui base de conhecimento conectada (legado).">
                  <i class="fa fa-file-pdf-o mr-1"></i>Knowledge
                </b-badge>
                <b-badge v-else-if="agent.knowledgeSource === 'database'" variant="success" pill class="ml-1"
                  v-b-tooltip.hover title="Possui base de conhecimento conectada.">
                  <i class="fa fa-file-pdf-o mr-1"></i>Knowledge (DB)
                </b-badge>
              </div>
              <b-button size="sm" variant="outline-secondary" @click.stop="confirmDuplicate(agent)"
                v-b-tooltip.hover title="Duplicar esta GenIA">
                <i class="fa fa-copy"></i>
              </b-button>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Agent Editor (edit or create mode) -->
      <div v-else-if="editingAgent || creatingAgent">
        <!-- Editor Header -->
        <b-row class="mb-3 align-items-center">
          <b-col>
            <b-button variant="outline-secondary" size="sm" @click="closeEditor" class="mr-2">
              <i class="fa fa-arrow-left"></i> Voltar
            </b-button>
            <span class="h6 mb-0">{{ creatingAgent ? 'Nova GenIA' : (form.label || editingAgent) }}</span>
            <template v-if="agentDetail && !creatingAgent">
              <b-badge v-if="agentDetail.isDynamic" variant="primary" class="ml-2">Criada</b-badge>
              <b-badge v-if="!agentDetail.active" variant="danger" class="ml-2">Desativada</b-badge>
              <b-badge v-else variant="success" class="ml-2">Ativa</b-badge>
              <b-badge v-if="agentDetail.hasOverride && !agentDetail.isDynamic" variant="warning" class="ml-2">Customizada</b-badge>
              <b-form-checkbox switch size="sm" class="ml-2 d-inline-block"
                :checked="agentDetail.active" @change="toggleActiveFromEditor($event)"
                v-b-tooltip.hover :title="agentDetail.active ? 'Desativar esta GenIA' : 'Reativar esta GenIA'" />
            </template>
          </b-col>
          <b-col cols="auto">
            <!-- Reset: only for native agents with override -->
            <b-button v-if="agentDetail && agentDetail.hasOverride && !agentDetail.isDynamic && !creatingAgent"
              variant="outline-secondary" size="sm" @click="confirmReset" class="mr-2">
              <i class="fa fa-undo"></i> Resetar
            </b-button>
            <!-- Delete: for any agent with override or dynamic -->
            <b-button v-if="agentDetail && (agentDetail.hasOverride || agentDetail.isDynamic) && !creatingAgent"
              variant="outline-danger" size="sm" @click="confirmDelete" class="mr-2">
              <i class="fa fa-trash"></i> Excluir
            </b-button>
            <!-- Save or Create -->
            <b-button variant="primary" size="sm" @click="creatingAgent ? submitCreate() : saveAgent()" :disabled="saving">
              <b-spinner v-if="saving" small class="mr-1" />
              <i v-else class="fa fa-save mr-1"></i> {{ creatingAgent ? 'Criar' : 'Salvar' }}
            </b-button>
          </b-col>
        </b-row>

        <!-- Editor Form -->
        <b-card class="shadow-sm mb-3">
          <b-form>
            <!-- Nome -->
            <b-form-group label-for="agent-label">
              <template #label>
                Nome <i class="fa fa-question-circle text-muted ml-1" v-b-tooltip.hover title="Nome exibido para o usuario no chat."></i>
              </template>
              <b-form-input id="agent-label" v-model="form.label" maxlength="100"
                placeholder="Nome exibido no chat" />
            </b-form-group>

            <!-- Descricao -->
            <b-form-group label-for="agent-desc">
              <template #label>
                Descricao <i class="fa fa-question-circle text-muted ml-1" v-b-tooltip.hover title="Resumo curto para identificar a especialidade da GenIA."></i>
              </template>
              <b-form-input id="agent-desc" v-model="form.description" maxlength="500"
                placeholder="Descricao curta do agente" />
            </b-form-group>

            <!-- Instrucoes -->
            <b-form-group label-for="agent-instructions"
              :description="`${(form.instructions || '').length} caracteres`">
              <template #label>
                Instrucoes <i class="fa fa-question-circle text-muted ml-1" v-b-tooltip.hover title="Prompt principal que define o comportamento e o foco da GenIA."></i>
              </template>
              <b-form-textarea id="agent-instructions" v-model="form.instructions" rows="12" max-rows="30"
                placeholder="Prompt de sistema do agente..." />
            </b-form-group>

            <!-- Welcome Message -->
            <b-form-group label-for="agent-welcome">
              <template #label>
                Mensagem de boas-vindas <i class="fa fa-question-circle text-muted ml-1" v-b-tooltip.hover title="Texto mostrado quando o usuario abre o chat."></i>
              </template>
              <b-form-textarea id="agent-welcome" v-model="form.welcomeMessage" rows="3" max-rows="6"
                placeholder="Mensagem exibida quando o usuario abre o chat" />
            </b-form-group>

            <!-- Ice Breakers -->
            <b-form-group>
              <template #label>
                Quebra-gelos <i class="fa fa-question-circle text-muted ml-1" v-b-tooltip.hover title="Sugestoes de perguntas iniciais para facilitar o primeiro uso."></i>
              </template>
              <div v-for="(ib, idx) in form.iceBreakers" :key="idx" class="d-flex mb-1">
                <b-form-input v-model="form.iceBreakers[idx]" size="sm" class="mr-1" />
                <b-button size="sm" variant="outline-danger" @click="removeIceBreaker(idx)">
                  <i class="fa fa-times"></i>
                </b-button>
              </div>
              <b-button size="sm" variant="outline-secondary" @click="addIceBreaker">
                <i class="fa fa-plus mr-1"></i> Adicionar
              </b-button>
            </b-form-group>
          </b-form>
        </b-card>

        <!-- Advanced Settings (collapsible) -->
        <b-card class="shadow-sm mb-3">
          <b-button v-b-toggle.advanced-settings variant="link" class="p-0 text-decoration-none">
            <i class="fa fa-cog mr-1"></i> Configuracoes Avancadas
            <i class="fa fa-chevron-down ml-1"></i>
          </b-button>

          <b-collapse id="advanced-settings" class="mt-3">
            <b-row>
              <!-- Modelo -->
              <b-col md="6">
                <b-form-group label-for="agent-model">
                  <template #label>
                    Modelo <i class="fa fa-question-circle text-muted ml-1" v-b-tooltip.hover title="Modelo de IA usado. Modelos mais avancados podem custar mais ou responder de forma diferente."></i>
                  </template>
                  <b-form-select id="agent-model" v-model="form.model" :options="modelOptions" />
                </b-form-group>
              </b-col>

              <!-- Temperatura -->
              <b-col md="6">
                <b-form-group label-for="agent-temp"
                  :description="isGpt5 ? 'Ignorado para GPT-5' : `Valor: ${form.temperature}`">
                  <template #label>
                    Temperatura <i class="fa fa-question-circle text-muted ml-1" v-b-tooltip.hover title="Controla a variacao da resposta. Menor = mais objetiva. Maior = mais criativa."></i>
                  </template>
                  <b-form-input id="agent-temp" v-model.number="form.temperature" type="range"
                    min="0" max="2" step="0.1" :disabled="isGpt5" />
                </b-form-group>
              </b-col>
            </b-row>

            <b-row>
              <!-- Max Output Tokens -->
              <b-col md="4">
                <b-form-group label-for="agent-tokens">
                  <template #label>
                    Max Output Tokens <i class="fa fa-question-circle text-muted ml-1" v-b-tooltip.hover title="Limite maximo de tamanho da resposta. Valores maiores permitem respostas mais longas, mas aumentam custo e tempo."></i>
                  </template>
                  <b-form-input id="agent-tokens" v-model.number="form.maxOutputTokens" type="number"
                    min="1" max="128000" placeholder="Ex: 4000" />
                </b-form-group>
              </b-col>

              <!-- Max Search Results -->
              <b-col md="4">
                <b-form-group label-for="agent-results">
                  <template #label>
                    Max Search Results <i class="fa fa-question-circle text-muted ml-1" v-b-tooltip.hover title="Quantidade de trechos buscados nos documentos. Mais resultados ampliam contexto, mas podem deixar a resposta mais lenta."></i>
                  </template>
                  <b-form-input id="agent-results" v-model.number="form.maxNumResults" type="number"
                    min="1" max="50" placeholder="Ex: 5" />
                </b-form-group>
              </b-col>

              <!-- Reasoning -->
              <b-col md="4">
                <b-form-group label-for="agent-reasoning">
                  <template #label>
                    Reasoning <i class="fa fa-question-circle text-muted ml-1" v-b-tooltip.hover title="Nivel de raciocinio do modelo. Melhora respostas complexas, mas aumenta latencia e custo."></i>
                  </template>
                  <b-form-select id="agent-reasoning" v-model="form.reasoningEffort" :options="reasoningOptions" />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Doc Fallback Query -->
            <b-form-group label-for="agent-fallback">
              <template #label>
                Doc Fallback Query <i class="fa fa-question-circle text-muted ml-1" v-b-tooltip.hover title="Consulta usada quando o usuario envia um arquivo sem pergunta clara. Orienta a analise inicial do documento."></i>
              </template>
              <b-form-input id="agent-fallback" v-model="form.docFallbackQuery" maxlength="500"
                placeholder="Analise exaustivamente o documento enviado..." />
            </b-form-group>
          </b-collapse>
        </b-card>

        <!-- Knowledge Section (hidden during creation — agent must exist first) -->
        <template v-if="!creatingAgent">
        <b-card class="shadow-sm mb-3">
          <h6 class="mb-3">
            <i class="fa fa-book mr-1"></i> Conhecimento
            <i class="fa fa-question-circle text-muted ml-1" v-b-tooltip.hover title="Arquivos e documentos usados pela GenIA como base para responder perguntas."></i>
          </h6>

          <!-- Legacy: vector store from vector-stores.json -->
          <div v-if="agentDetail && agentDetail.knowledgeSource === 'legacy'">
            <b-alert show variant="info" class="mb-2">
              <i class="fa fa-info-circle mr-1"></i>
              Vector Store conectado (legado)
              <br>
              <small class="text-muted">ID: {{ agentDetail.vector_store_ids && agentDetail.vector_store_ids[0] }}</small>
            </b-alert>
            <p class="text-muted small">
              Os arquivos deste agente foram indexados via script. Migre para poder gerenciar arquivos pelo configurador.
            </p>
            <b-button size="sm" variant="outline-primary" @click="confirmMigrate" :disabled="migrating">
              <b-spinner v-if="migrating" small class="mr-1" />
              <i v-else class="fa fa-exchange mr-1"></i> Migrar para gerenciavel
            </b-button>
          </div>

          <!-- Database: has kbFiles metadata -->
          <div v-else-if="agentDetail && agentDetail.knowledgeSource === 'database' && agentDetail.kbFiles && agentDetail.kbFiles.length > 0">
            <b-list-group flush>
              <b-list-group-item v-for="(file, idx) in agentDetail.kbFiles" :key="idx"
                class="d-flex justify-content-between align-items-center py-2">
                <div>
                  <i class="fa fa-file-pdf-o text-danger mr-1"></i>
                  <span class="small">{{ file.name }}</span>
                  <b-badge v-if="file.status === 'completed'" variant="success" pill class="ml-1">OK</b-badge>
                  <b-badge v-else-if="file.status === 'failed'" variant="danger" pill class="ml-1">Falhou</b-badge>
                  <b-badge v-else variant="warning" pill class="ml-1">{{ file.status }}</b-badge>
                </div>
                <div class="d-flex align-items-center">
                  <small class="text-muted mr-2">{{ formatFileSize(file.size) }}</small>
                  <b-button size="sm" variant="outline-danger" @click="confirmRemoveFile(file)"
                    :disabled="removingFileId === file.vectorStoreFileId">
                    <b-spinner v-if="removingFileId === file.vectorStoreFileId" small />
                    <i v-else class="fa fa-times"></i>
                  </b-button>
                </div>
              </b-list-group-item>
            </b-list-group>

            <!-- Upload area -->
            <div class="mt-3">
              <input ref="fileInput" type="file" accept=".pdf,.docx" multiple
                class="d-none" @change="handleFileSelect" />
              <b-button size="sm" variant="outline-primary" @click="$refs.fileInput.click()" :disabled="uploading">
                <b-spinner v-if="uploading" small class="mr-1" />
                <i v-else class="fa fa-upload mr-1"></i> Carregar arquivos
              </b-button>
              <small class="text-muted ml-2">PDF ou DOCX, max 50MB por arquivo</small>
            </div>

            <!-- Upload progress -->
            <div v-if="uploading" class="mt-2">
              <b-progress :value="uploadProgress" :max="100" animated variant="primary" class="mb-2" />
              <div v-for="(uf, idx) in uploadingFiles" :key="idx" class="small">
                <i v-if="uf.status === 'completed'" class="fa fa-check text-success mr-1"></i>
                <i v-else-if="uf.status === 'failed'" class="fa fa-times text-danger mr-1"></i>
                <b-spinner v-else small class="mr-1" />
                {{ uf.name }} — {{ uf.status === 'uploading' ? 'Enviando...' : uf.status === 'indexing' ? 'Indexando...' : uf.status }}
              </div>
            </div>
          </div>

          <!-- Database: vectorStoreId exists but no kbFiles metadata yet -->
          <div v-else-if="agentDetail && agentDetail.knowledgeSource === 'database' && agentDetail.vectorStoreId">
            <b-alert show variant="info" class="mb-2">
              <i class="fa fa-info-circle mr-1"></i>
              Vector Store conectado (via configurador)
              <br>
              <small class="text-muted">ID: {{ agentDetail.vectorStoreId }}</small>
            </b-alert>

            <!-- Upload area -->
            <div class="mt-2">
              <input ref="fileInputEmpty" type="file" accept=".pdf,.docx" multiple
                class="d-none" @change="handleFileSelect" />
              <b-button size="sm" variant="outline-primary" @click="$refs.fileInputEmpty.click()" :disabled="uploading">
                <b-spinner v-if="uploading" small class="mr-1" />
                <i v-else class="fa fa-upload mr-1"></i> Carregar arquivos
              </b-button>
              <small class="text-muted ml-2">PDF ou DOCX, max 50MB por arquivo</small>
            </div>

            <!-- Upload progress -->
            <div v-if="uploading" class="mt-2">
              <b-progress :value="uploadProgress" :max="100" animated variant="primary" class="mb-2" />
              <div v-for="(uf, idx) in uploadingFiles" :key="idx" class="small">
                <b-spinner v-if="uf.status !== 'completed' && uf.status !== 'failed'" small class="mr-1" />
                <i v-else-if="uf.status === 'completed'" class="fa fa-check text-success mr-1"></i>
                <i v-else class="fa fa-times text-danger mr-1"></i>
                {{ uf.name }} — {{ uf.status }}
              </div>
            </div>
          </div>

          <!-- No knowledge -->
          <div v-else>
            <p class="text-muted small mb-2">Nenhum conhecimento configurado para este agente.</p>

            <!-- Upload area for first upload -->
            <input ref="fileInputNew" type="file" accept=".pdf,.docx" multiple
              class="d-none" @change="handleFileSelect" />
            <b-button size="sm" variant="outline-primary" @click="$refs.fileInputNew.click()" :disabled="uploading">
              <b-spinner v-if="uploading" small class="mr-1" />
              <i v-else class="fa fa-upload mr-1"></i> Carregar arquivos
            </b-button>
            <small class="text-muted ml-2">PDF ou DOCX, max 50MB por arquivo</small>

            <!-- Upload progress -->
            <div v-if="uploading" class="mt-2">
              <b-progress :value="uploadProgress" :max="100" animated variant="primary" class="mb-2" />
              <div v-for="(uf, idx) in uploadingFiles" :key="idx" class="small">
                <b-spinner v-if="uf.status !== 'completed' && uf.status !== 'failed'" small class="mr-1" />
                <i v-else-if="uf.status === 'completed'" class="fa fa-check text-success mr-1"></i>
                <i v-else class="fa fa-times text-danger mr-1"></i>
                {{ uf.name }} — {{ uf.status }}
              </div>
            </div>
          </div>

        </b-card>
        </template>

        <!-- Bottom action bar (duplicates top buttons for long pages) -->
        <b-row class="mt-3 mb-3 align-items-center">
          <b-col>
            <b-button variant="outline-secondary" size="sm" @click="closeEditor">
              <i class="fa fa-arrow-left"></i> Voltar
            </b-button>
          </b-col>
          <b-col cols="auto">
            <b-button v-if="agentDetail && agentDetail.hasOverride && !agentDetail.isDynamic && !creatingAgent"
              variant="outline-secondary" size="sm" @click="confirmReset" class="mr-2">
              <i class="fa fa-undo"></i> Resetar
            </b-button>
            <b-button v-if="agentDetail && (agentDetail.hasOverride || agentDetail.isDynamic) && !creatingAgent"
              variant="outline-danger" size="sm" @click="confirmDelete" class="mr-2">
              <i class="fa fa-trash"></i> Excluir
            </b-button>
            <b-button variant="primary" size="sm" @click="creatingAgent ? submitCreate() : saveAgent()" :disabled="saving">
              <b-spinner v-if="saving" small class="mr-1" />
              <i v-else class="fa fa-save mr-1"></i> {{ creatingAgent ? 'Criar' : 'Salvar' }}
            </b-button>
          </b-col>
        </b-row>

      </div>

    </b-container>
  </div>
</template>

<script>
import {
  getAgents, getAgent, updateAgent, resetAgent,
  uploadKnowledgeFiles, removeKnowledgeFile, migrateKnowledge,
  createAgent, duplicateAgent, deleteAgent,
} from '../../services/adminAgents';

export default {
  name: 'AgentConfigurator',
  data() {
    return {
      loading: false,
      saving: false,
      creatingAgent: false,
      uploading: false,
      uploadProgress: 0,
      uploadingFiles: [],
      migrating: false,
      removingFileId: null,
      agents: [],
      editingAgent: null,
      agentDetail: null,
      form: {
        label: '',
        description: '',
        instructions: '',
        welcomeMessage: '',
        model: 'gpt-4o-mini',
        temperature: 0.3,
        maxOutputTokens: null,
        maxNumResults: null,
        reasoningEffort: null,
        iceBreakers: [],
        docFallbackQuery: '',
      },
      modelOptions: [
        { value: 'gpt-4o-mini', text: 'GPT-4o Mini' },
        { value: 'gpt-4o', text: 'GPT-4o' },
        { value: 'gpt-4.1-nano', text: 'GPT-4.1 Nano' },
        { value: 'gpt-4.1-mini', text: 'GPT-4.1 Mini' },
        { value: 'gpt-4.1', text: 'GPT-4.1' },
        { value: 'gpt-5-mini', text: 'GPT-5 Mini' },
        { value: 'gpt-5', text: 'GPT-5' },
        { value: 'gpt-5.4', text: 'GPT-5.4' },
        { value: 'gpt-5.4-mini', text: 'GPT-5.4 Mini' },
        { value: 'gpt-5.5', text: 'GPT-5.5' },
        { value: 'gpt-5.6-sol', text: 'GPT-5.6 Sol' },
        { value: 'gpt-5.6-luna', text: 'GPT-5.6 Luna' },
        { value: 'gpt-5.6-terra', text: 'GPT-5.6 Terra' },
        { value: 'o3-mini', text: 'o3-mini' },
        { value: 'o3', text: 'o3' },
        { value: 'o4-mini', text: 'o4-mini' },
      ],
      reasoningOptions: [
        { value: null, text: 'Desativado' },
        { value: 'low', text: 'Low' },
        { value: 'medium', text: 'Medium' },
        { value: 'high', text: 'High' },
      ],
    };
  },
  computed: {
    isGpt5() {
      return this.form.model && this.form.model.startsWith('gpt-5');
    },
  },
  async mounted() {
    await this.loadAgents();
  },
  methods: {
    async loadAgents() {
      this.loading = true;
      try {
        this.agents = await getAgents();
      } catch (err) {
        this.$swal('Erro', 'Nao foi possivel carregar os agentes.', 'error');
      } finally {
        this.loading = false;
      }
    },

    async openEditor(agentId) {
      this.loading = true;
      try {
        const detail = await getAgent(agentId);
        this.agentDetail = detail;
        this.editingAgent = agentId;

        // Populate form from merged config
        this.form.label = detail.label || '';
        this.form.description = detail.description || '';
        this.form.instructions = detail.instructions || '';
        this.form.welcomeMessage = detail.welcome_message || '';
        this.form.model = detail.model || 'gpt-4o-mini';
        this.form.temperature = detail.temperature != null ? detail.temperature : 0.3;
        this.form.maxOutputTokens = detail.max_output_tokens || null;
        this.form.maxNumResults = detail.max_num_results || null;
        this.form.reasoningEffort = (detail.reasoning && detail.reasoning.effort) || null;
        this.form.iceBreakers = (detail.ice_breakers && [...detail.ice_breakers]) || [];
        this.form.docFallbackQuery = detail.doc_fallback_query || '';
      } catch (err) {
        this.$swal('Erro', 'Nao foi possivel carregar o agente.', 'error');
      } finally {
        this.loading = false;
      }
    },

    closeEditor() {
      this.editingAgent = null;
      this.creatingAgent = false;
      this.agentDetail = null;
      this.loadAgents();
    },

    // ---- B5B: Create / Duplicate / Delete ----

    startCreate() {
      this.creatingAgent = true;
      this.editingAgent = null;
      this.agentDetail = null;
      this.form = {
        label: '',
        description: '',
        instructions: '',
        welcomeMessage: '',
        model: 'gpt-4o-mini',
        temperature: 0.3,
        maxOutputTokens: null,
        maxNumResults: null,
        reasoningEffort: null,
        iceBreakers: [],
        docFallbackQuery: '',
      };
    },

    async submitCreate() {
      if (!this.form.label || this.form.label.trim() === '') {
        this.$swal('Atencao', 'Nome e obrigatorio.', 'warning');
        return;
      }
      if (!this.form.instructions || this.form.instructions.trim() === '') {
        this.$swal('Atencao', 'Instrucoes sao obrigatorias.', 'warning');
        return;
      }

      this.saving = true;
      try {
        const numOrNull = (v) => (v === '' || v === null || v === undefined) ? null : v;
        const payload = {
          label: this.form.label,
          description: this.form.description || null,
          instructions: this.form.instructions,
          welcomeMessage: this.form.welcomeMessage || null,
          model: this.form.model,
          temperature: this.form.temperature,
          maxOutputTokens: numOrNull(this.form.maxOutputTokens),
          maxNumResults: numOrNull(this.form.maxNumResults),
          reasoning: this.form.reasoningEffort ? { effort: this.form.reasoningEffort } : null,
          iceBreakers: this.form.iceBreakers.filter(ib => ib.trim() !== ''),
          docFallbackQuery: this.form.docFallbackQuery || null,
        };

        const agent = await createAgent(payload);
        this.$swal({
          title: 'GenIA criada!',
          text: `"${agent.label}" esta pronta para uso.`,
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });

        // Switch to edit mode for the new agent
        this.creatingAgent = false;
        await this.openEditor(agent.id);
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.error) || 'Erro ao criar.';
        this.$swal('Erro', msg, 'error');
      } finally {
        this.saving = false;
      }
    },

    async confirmDuplicate(agent) {
      const result = await this.$swal({
        title: 'Duplicar GenIA?',
        text: `Uma copia de "${agent.label}" sera criada. Knowledge nao e copiado.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, duplicar',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed) {
        try {
          const newAgent = await duplicateAgent(agent.id);
          this.$swal({
            title: 'Duplicada!',
            text: `"${newAgent.label}" criada com sucesso.`,
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          });
          await this.loadAgents();
          await this.openEditor(newAgent.id);
        } catch (err) {
          const msg = (err.response && err.response.data && err.response.data.error) || 'Erro ao duplicar.';
          this.$swal('Erro', msg, 'error');
        }
      }
    },

    async confirmDelete() {
      if (!this.agentDetail) return;

      const isDynamic = this.agentDetail.isDynamic;
      const text = isDynamic
        ? `"${this.agentDetail.label}" sera excluida permanentemente, incluindo arquivos de conhecimento.`
        : `A configuracao de "${this.agentDetail.label}" sera removida e ela voltara ao padrao original. Arquivos de conhecimento gerenciados serao excluidos.`;

      const result = await this.$swal({
        title: 'Excluir GenIA?',
        html: `${text}<br><br><strong>Digite EXCLUIR para confirmar:</strong>`,
        icon: 'warning',
        input: 'text',
        inputPlaceholder: 'EXCLUIR',
        showCancelButton: true,
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545',
        inputValidator: (value) => {
          if (value !== 'EXCLUIR') return 'Digite EXCLUIR para confirmar.';
        },
      });

      if (result.isConfirmed) {
        try {
          await deleteAgent(this.editingAgent);
          this.$swal({
            title: isDynamic ? 'Excluida!' : 'Removida!',
            text: isDynamic ? 'GenIA excluida permanentemente.' : 'Configuracao removida. GenIA voltou ao padrao.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          });
          this.closeEditor();
        } catch (err) {
          const msg = (err.response && err.response.data && err.response.data.error) || 'Erro ao excluir.';
          this.$swal('Erro', msg, 'error');
        }
      }
    },

    addIceBreaker() {
      this.form.iceBreakers.push('');
    },

    removeIceBreaker(idx) {
      this.form.iceBreakers.splice(idx, 1);
    },

    async saveAgent() {
      // Basic frontend validation
      if (!this.form.instructions || this.form.instructions.trim() === '') {
        this.$swal('Atencao', 'Instrucoes nao podem ser vazias.', 'warning');
        return;
      }

      this.saving = true;
      try {
        const numOrNull = (v) => (v === '' || v === null || v === undefined) ? null : v;

        const payload = {
          label: this.form.label || null,
          description: this.form.description || null,
          instructions: this.form.instructions,
          welcomeMessage: this.form.welcomeMessage || null,
          model: this.form.model,
          temperature: this.isGpt5 ? null : this.form.temperature,
          maxOutputTokens: numOrNull(this.form.maxOutputTokens),
          maxNumResults: numOrNull(this.form.maxNumResults),
          reasoning: this.form.reasoningEffort ? { effort: this.form.reasoningEffort } : null,
          iceBreakers: this.form.iceBreakers.filter(ib => ib.trim() !== ''),
          docFallbackQuery: this.form.docFallbackQuery || null,
        };

        await updateAgent(this.editingAgent, payload);

        this.$swal({
          title: 'Salvo!',
          text: 'Configuracao do agente atualizada.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });

        // Refresh detail
        await this.openEditor(this.editingAgent);
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.error)
          || 'Nao foi possivel salvar.';
        this.$swal('Erro', msg, 'error');
      } finally {
        this.saving = false;
      }
    },

    async confirmReset() {
      const result = await this.$swal({
        title: 'Resetar para padrao?',
        text: 'Todas as customizacoes deste agente serao removidas e ele voltara a configuracao original.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, resetar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545',
      });

      if (result.isConfirmed) {
        try {
          await resetAgent(this.editingAgent);
          this.$swal({
            title: 'Resetado!',
            text: 'Agente voltou ao padrao.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          });
          await this.openEditor(this.editingAgent);
        } catch (err) {
          this.$swal('Erro', 'Nao foi possivel resetar.', 'error');
        }
      }
    },

    // ---- Active Toggle ----

    async toggleActive(agent, newValue) {
      // Deactivating requires confirmation; reactivating does not
      if (!newValue) {
        const result = await this.$swal({
          title: 'Desativar GenIA?',
          text: `"${agent.label}" nao aparecera mais no chat para os usuarios.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, desativar',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#dc3545',
        });
        if (!result.isConfirmed) {
          // Checkbox already flipped visually — reload to revert
          await this.loadAgents();
          return;
        }
      }

      try {
        await updateAgent(agent.id, { active: newValue });
        this.$swal({
          title: newValue ? 'Reativada!' : 'Desativada!',
          text: newValue ? 'GenIA visivel no chat novamente.' : 'GenIA ocultada do chat.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
        await this.loadAgents();
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.error) || 'Erro ao alterar status.';
        this.$swal('Erro', msg, 'error');
        await this.loadAgents(); // Revert visual state on error too
      }
    },

    async toggleActiveFromEditor(newValue) {
      if (!this.agentDetail) return;
      // Deactivating requires confirmation; reactivating does not
      if (!newValue) {
        const result = await this.$swal({
          title: 'Desativar GenIA?',
          text: `"${this.agentDetail.label}" nao aparecera mais no chat para os usuarios.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, desativar',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#dc3545',
        });
        if (!result.isConfirmed) {
          // Checkbox already flipped visually — reload to revert
          await this.openEditor(this.editingAgent);
          return;
        }
      }

      try {
        await updateAgent(this.editingAgent, { active: newValue });
        this.$swal({
          title: newValue ? 'Reativada!' : 'Desativada!',
          text: newValue ? 'GenIA visivel no chat novamente.' : 'GenIA ocultada do chat.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
        });
        await this.openEditor(this.editingAgent);
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.error) || 'Erro ao alterar status.';
        this.$swal('Erro', msg, 'error');
      }
    },

    // ---- Knowledge Management ----

    async handleFileSelect(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      // Frontend validation
      const maxSize = 50 * 1024 * 1024;
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

      for (const file of files) {
        if (file.size > maxSize) {
          this.$swal('Erro', `Arquivo "${file.name}" excede 50MB.`, 'error');
          event.target.value = '';
          return;
        }
        if (!allowedTypes.includes(file.type)) {
          this.$swal('Erro', `Arquivo "${file.name}" nao e PDF ou DOCX.`, 'error');
          event.target.value = '';
          return;
        }
      }

      this.uploading = true;
      this.uploadProgress = 0;
      this.uploadingFiles = Array.from(files).map(f => ({ name: f.name, status: 'uploading' }));

      try {
        const result = await uploadKnowledgeFiles(this.editingAgent, files, (progressEvent) => {
          if (progressEvent.total) {
            this.uploadProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
          }
        });

        // Update status from results
        if (result.results) {
          this.uploadingFiles = result.results.map(r => ({
            name: r.name,
            status: r.status === 'completed' ? 'completed' : 'failed',
          }));
        }

        const completed = result.results ? result.results.filter(r => r.status === 'completed').length : 0;
        const failed = result.results ? result.results.filter(r => r.status === 'failed').length : 0;

        if (failed === 0) {
          this.$swal({
            title: 'Upload concluido!',
            text: `${completed} arquivo(s) indexado(s) com sucesso.`,
            icon: 'success',
            timer: 3000,
            showConfirmButton: false,
          });
        } else {
          this.$swal('Atencao', `${completed} indexado(s), ${failed} falharam.`, 'warning');
        }

        // Refresh detail
        await this.openEditor(this.editingAgent);
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.error) || 'Erro no upload.';
        this.$swal('Erro', msg, 'error');
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
        this.uploadingFiles = [];
        event.target.value = '';
      }
    },

    async confirmRemoveFile(file) {
      const result = await this.$swal({
        title: 'Remover arquivo?',
        text: `"${file.name}" sera removido da base de conhecimento e excluido da OpenAI.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc3545',
      });

      if (result.isConfirmed) {
        this.removingFileId = file.vectorStoreFileId;
        try {
          await removeKnowledgeFile(this.editingAgent, file.vectorStoreFileId);
          this.$swal({
            title: 'Removido!',
            text: 'Arquivo removido da base de conhecimento.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false,
          });
          await this.openEditor(this.editingAgent);
        } catch (err) {
          const msg = (err.response && err.response.data && err.response.data.error) || 'Erro ao remover.';
          this.$swal('Erro', msg, 'error');
        } finally {
          this.removingFileId = null;
        }
      }
    },

    async confirmMigrate() {
      const result = await this.$swal({
        title: 'Migrar knowledge?',
        html: 'O vector store legado sera transferido para o gerenciamento pelo configurador.<br><br><small class="text-muted">Apos a migracao, voce podera adicionar e remover arquivos por aqui.</small>',
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: 'Sim, migrar',
        cancelButtonText: 'Cancelar',
      });

      if (result.isConfirmed) {
        this.migrating = true;
        try {
          const data = await migrateKnowledge(this.editingAgent);
          this.$swal({
            title: 'Migrado!',
            text: data.message || 'Migracao concluida.',
            icon: 'success',
            timer: 3000,
            showConfirmButton: false,
          });
          await this.openEditor(this.editingAgent);
        } catch (err) {
          const msg = (err.response && err.response.data && err.response.data.error) || 'Erro na migracao.';
          this.$swal('Erro', msg, 'error');
        } finally {
          this.migrating = false;
        }
      }
    },

    formatFileSize(bytes) {
      if (!bytes) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
  },
};
</script>

<style scoped>
.agent-card {
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}
.agent-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}
.agent-inactive {
  opacity: 0.5;
}
.agent-inactive:hover {
  transform: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}
</style>
